import { ReactNode } from "react";
import { Capacitor } from "@capacitor/core";
import { cn } from "@/lib/utils";

interface NativeAppLayoutProps {
  children: ReactNode;
  /** Hide the native header bar */
  hideHeader?: boolean;
  /** Custom header title */
  headerTitle?: string;
  /** Custom header content (replaces title) */
  headerContent?: ReactNode;
  /** Additional class names for the content area */
  className?: string;
  /** Fill the entire screen height (default: true) */
  fillScreen?: boolean;
}

/**
 * Layout wrapper that provides native app styling on iOS/Android.
 * On web, it renders children directly without modification.
 *
 * Features:
 * - Edge-to-edge layout with proper safe area handling
 * - Full-height screens (no dead bottom space)
 * - Native-feeling header bar
 * - Smooth page entrance animation
 */
export function NativeAppLayout({
  children,
  hideHeader = false,
  headerTitle,
  headerContent,
  className,
  fillScreen = true,
}: NativeAppLayoutProps) {
  const isNative = Capacitor.isNativePlatform();

  // On web, just render children directly
  if (!isNative) {
    return <>{children}</>;
  }

  return (
    <div
      className={cn(
        "native-app-container bg-background font-body",
        fillScreen && "native-fill-screen"
      )}
    >
      {/* Native Header Bar with integrated safe area */}
      {!hideHeader && (headerTitle || headerContent) && (
        <header className="native-header sticky top-0 z-50 border-b border-border/60 bg-background/90">
          {/* Safe area spacer for notch / dynamic island */}
          <div
            className="bg-background"
            style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
          />
          <div className="flex h-[52px] items-center justify-center px-4">
            {headerContent || (
              <h1 className="truncate font-heading text-[18px] font-bold text-foreground">
                {headerTitle}
              </h1>
            )}
          </div>
        </header>
      )}

      {/* Safe area top for pages without header */}
      {hideHeader && (
        <div
          style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
          className="bg-background"
        />
      )}

      {/* Main Content — fills remaining space */}
      <main
        className={cn(
          "native-content native-page-enter flex-1",
          className
        )}
      >
        {children}
      </main>

      {/* Safe area bottom — no visible gap, just padding for home indicator */}
      <div
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        className="bg-background flex-shrink-0"
      />
    </div>
  );
}

/**
 * Hook to check if running in native app
 */
export function useIsNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}
